import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Menu, X, Bell, Search, Sun, Moon } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { SearchBox } from "@/components/SearchBox";
import { Avatar } from "@/components/Common";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export const NAV = [
  ["Home", "/"], ["Movies", "/movies"], ["TV Shows", "/tv-shows"], ["Anime", "/anime"], ["Where to Watch", "/where-to-watch"],
  ["Community", "/community"], ["My Watchlist", "/watchlist"], ["About Us", "/about"],
];

export function Logo({ compact }) {
  const { settings } = useAuth();
  return (
    <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 shrink-0">
      <img src={settings.logo_url} alt="Shivam Cine Core logo" className="h-11 w-11 rounded-xl bg-black object-contain ring-1 ring-amber-400/40" />
      {!compact && (
        <span className="leading-none">
          <span className="block font-display text-xl text-amber-400">SHIVAM</span>
          <span className="block font-display text-sm tracking-[0.25em] text-purple-400">CINE CORE</span>
        </span>
      )}
    </Link>
  );
}

function UserMenu() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  useEffect(() => { api.get("/me/notifications").then((r) => setUnread(r.data.unread)).catch(() => {}); }, []);
  return (
    <div className="flex items-center gap-2">
      <Link to="/notifications" data-testid="header-notifications" aria-label="Notifications" className="relative rounded-full p-2 hover:bg-white/5">
        <Bell className="h-5 w-5" />
        {unread > 0 && <span className="absolute right-1 top-1 h-2 w-2 rounded-full bg-purple-500" />}
      </Link>
      <DropdownMenu>
        <DropdownMenuTrigger data-testid="user-menu-trigger" className="rounded-full"><Avatar user={user} size={34} /></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 border-white/10 bg-[#12121A]">
          <p className="px-2 py-1.5 text-xs text-slate-500">@{user.username}</p>
          {[["Profile", `/u/${user.username}`, "menu-profile"], ["My Watchlist", "/watchlist", "menu-watchlist"], ["My Reviews", "/my-reviews", "menu-reviews"],
            ["Notifications", "/notifications", "menu-notifications"], ["Account Settings", "/settings", "menu-settings"]].map(([l, to, id]) => (
            <DropdownMenuItem key={to} data-testid={id} onClick={() => nav(to)}>{l}</DropdownMenuItem>
          ))}
          {user.role === "admin" && <DropdownMenuItem data-testid="menu-admin" className="text-amber-400" onClick={() => nav("/admin")}>Admin Dashboard</DropdownMenuItem>}
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="menu-signout" onClick={async () => { await logout(); nav("/"); }}>Sign Out</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function AuthButtons({ stacked }) {
  const loc = useLocation();
  const next = `?next=${encodeURIComponent(loc.pathname)}`;
  return (
    <div className={`flex gap-2 ${stacked ? "flex-col" : "items-center"}`}>
      <Link to={`/login${next}`} data-testid="header-signin" className="rounded-full px-4 py-2 text-sm font-semibold text-slate-200 hover:text-amber-400">Sign In</Link>
      <Link to="/register" data-testid="header-register" className="rounded-full bg-amber-400 px-4 py-2 text-center text-sm font-bold text-black hover:bg-amber-300 transition-colors">Create Account</Link>
    </div>
  );
}

function ThemeToggle() {
  const [light, setLight] = useState(() => localStorage.getItem("scc-theme") === "light");
  const toggle = () => {
    const next = !light;
    setLight(next);
    document.documentElement.classList.toggle("light", next);
    localStorage.setItem("scc-theme", next ? "light" : "dark");
  };
  return (
    <button onClick={toggle} data-testid="theme-toggle" aria-label={light ? "Switch to dark mode" : "Switch to day mode"}
      className="rounded-full border border-white/10 p-2 hover:border-amber-400 hover:text-amber-400 transition-colors">
      {light ? <Moon className="h-4.5 w-4.5 h-[18px] w-[18px]" /> : <Sun className="h-[18px] w-[18px]" />}
    </button>
  );
}

export function Header() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const linkCls = ({ isActive }) => `whitespace-nowrap text-[13px] font-semibold transition-colors ${isActive ? "text-amber-400" : "text-slate-300 hover:text-white"}`;
  return (
    <header className="sticky top-0 z-50 border-b border-white/5 bg-[#0A0A0E]/85 backdrop-blur-xl">
      <div className="flex h-16 items-center gap-4 px-4 sm:px-6 lg:px-12">
        <Logo />
        <nav className="ml-4 hidden items-center gap-5 xl:flex" aria-label="Main">
          {NAV.map(([l, to]) => <NavLink key={to} to={to} end={to === "/"} className={linkCls} data-testid={`nav-${to.replace("/", "") || "home"}`}>{l}</NavLink>)}
        </nav>
        <div className="ml-auto hidden w-64 lg:block"><SearchBox /></div>
        <button className="ml-auto rounded-full p-2 hover:bg-white/5 lg:hidden" aria-label="Search" data-testid="mobile-search-toggle" onClick={() => setSearchOpen((s) => !s)}><Search className="h-5 w-5" /></button>
        <ThemeToggle />
        <div className="hidden sm:block">{user ? <UserMenu /> : user === false ? <AuthButtons /> : null}</div>
        {user && <div className="sm:hidden"><UserMenu /></div>}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild><button className="rounded-full p-2 hover:bg-white/5 xl:hidden" aria-label="Menu" data-testid="mobile-menu-toggle">{open ? <X /> : <Menu />}</button></SheetTrigger>
          <SheetContent side="right" className="w-72 border-white/10 bg-[#0A0A0E]">
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <nav className="mt-8 flex flex-col gap-4">
              {NAV.map(([l, to]) => <NavLink key={to} to={to} end={to === "/"} onClick={() => setOpen(false)} className={({ isActive }) => `font-display text-2xl ${isActive ? "text-amber-400" : "text-slate-200"}`}>{l}</NavLink>)}
            </nav>
            {user === false && <div className="mt-8" onClick={() => setOpen(false)}><AuthButtons stacked /></div>}
          </SheetContent>
        </Sheet>
      </div>
      {searchOpen && <div className="px-4 pb-3 lg:hidden"><SearchBox onDone={() => setSearchOpen(false)} /></div>}
    </header>
  );
}
